import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Sparkles } from "lucide-react";
import { Navbar } from "../components/Navbar";
import { Footer } from "../components/Footer";
import { Marquee } from "../components/Marquee";
import { ServiceCard } from "../components/ServiceCard";
import { SERVICES, PORTFOLIO } from "../data/site";

export default function Home() {
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return SERVICES;
    return SERVICES.filter(
      (s) =>
        s.title.toLowerCase().includes(q) ||
        s.blurb.toLowerCase().includes(q) ||
        s.keywords.some((k) => k.includes(q))
    );
  }, [query]);

  const scrollToServices = () => {
    document.getElementById("services")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="App">
      <Navbar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[#F3F4F6]">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 pt-16 pb-20 sm:pt-24 sm:pb-28">
          <div className="grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-7">
              <span className="inline-flex items-center gap-2 brutal-border bg-white px-3 py-1 text-xs font-bold uppercase tracking-[0.2em] brutal-shadow-sm">
                <Sparkles size={14} strokeWidth={2.5} className="text-[#3E6FA8]" />
                Graphic Design Studio
              </span>
              <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-medium leading-[1.05] tracking-tight mt-6">
                Made by
                <br />
                <span className="text-[#3E6FA8] italic uppercase">My Gifted</span>{" "}
                <span className="text-[#475569] uppercase">Hands</span>
              </h1>
              <p className="mt-6 max-w-xl text-lg font-medium text-[#1F2530]/80">
                Logos, business cards, commissioned portraits, comic books, children's
                books & more — designed bold, drawn by hand, made just for you.
              </p>

              {/* SEARCH / START ORDER */}
              <div className="mt-8 max-w-xl">
                <label className="block text-xs font-bold uppercase tracking-[0.2em] mb-2">
                  Start your order — what do you need?
                </label>
                <div className="flex flex-col sm:flex-row gap-3">
                  <div className="flex-1 flex items-center gap-3 brutal-border bg-white px-4 py-3 brutal-shadow-sm">
                    <Search size={20} strokeWidth={2.5} className="text-[#1F2530]/60 shrink-0" />
                    <input
                      data-testid="service-search-input"
                      value={query}
                      onChange={(e) => setQuery(e.target.value)}
                      placeholder="Try 'logo', 'portrait', 'comic'…"
                      className="w-full bg-transparent outline-none font-medium placeholder:text-[#1F2530]/40"
                    />
                  </div>
                  <button
                    data-testid="hero-search-button"
                    onClick={scrollToServices}
                    className="brutal-border brutal-shadow-sm brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider px-6 py-3 flex items-center justify-center gap-2"
                  >
                    Browse <ArrowRight size={18} strokeWidth={2.5} />
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {["Logo", "Portrait", "Comic", "Business Card"].map((chip) => (
                    <button
                      key={chip}
                      data-testid={`hero-chip-${chip.toLowerCase().replace(" ", "-")}`}
                      onClick={() => {
                        setQuery(chip);
                        scrollToServices();
                      }}
                      className="brutal-border bg-white px-3 py-1 text-xs font-bold uppercase tracking-wider hover:bg-[#1F2530] hover:text-white transition-colors"
                    >
                      {chip}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div className="lg:col-span-5">
              <div className="relative">
                <img
                  data-testid="hero-image"
                  src="https://customer-assets.emergentagent.com/job_hands-creative/artifacts/x9aiyll2_124F0878-AFD1-404E-9307-F9506E17E5E6.png"
                  alt="My Gifted Hands neon logo on brick wall"
                  className="w-full aspect-[4/5] object-cover brutal-border brutal-shadow"
                />
                <div className="absolute -bottom-4 -left-4 brutal-border bg-[#E5E7EB] px-4 py-2 font-display text-2xl rotate-[-4deg]">
                  Custom Art & Designs
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Marquee />

      {/* SERVICES / INFO */}
      <section id="services" className="bg-[#F3F4F6] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#3E6FA8]">
                What We Do
              </span>
              <h2 className="font-display text-5xl sm:text-6xl leading-none mt-2">
                Services & Craft
              </h2>
            </div>
            <p className="max-w-sm font-medium text-[#1F2530]/70">
              {query
                ? `${filtered.length} result${filtered.length === 1 ? "" : "s"} for “${query}”`
                : "Search above to filter, or explore everything My Gifted Hands creates."}
            </p>
          </div>

          {filtered.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8" data-testid="services-grid">
              {filtered.map((s) => (
                <ServiceCard key={s.id} service={s} />
              ))}
            </div>
          ) : (
            <div
              data-testid="services-empty"
              className="brutal-border bg-white p-10 text-center brutal-shadow"
            >
              <p className="font-display text-3xl">No match for “{query}”</p>
              <p className="mt-2 font-medium text-[#1F2530]/70">
                We do tons of custom work — just{" "}
                <Link to="/contact" className="underline font-bold">
                  reach out
                </Link>{" "}
                and ask!
              </p>
              <button
                data-testid="clear-search"
                onClick={() => setQuery("")}
                className="mt-5 brutal-border brutal-shadow-sm brutal-press bg-[#E5E7EB] font-extrabold uppercase tracking-wider px-6 py-3"
              >
                Show all services
              </button>
            </div>
          )}
        </div>
      </section>

      {/* PORTFOLIO */}
      <section id="work" className="bg-white border-y border-[#E3E5E9] py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10">
          <div className="mb-10">
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#475569]">
              Selected Work
            </span>
            <h2 className="font-display text-5xl sm:text-6xl leading-none mt-2">
              The Portfolio
            </h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 sm:gap-8">
            {PORTFOLIO.map((p, i) => (
              <div
                key={p.title}
                data-testid={`portfolio-item-${i}`}
                className="group relative overflow-hidden brutal-border brutal-shadow"
              >
                <img
                  src={p.img}
                  alt={p.title}
                  className="w-full aspect-square object-cover transition-transform duration-300 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-[#1F2530]/0 group-hover:bg-[#1F2530]/80 transition-colors duration-300 flex flex-col items-center justify-center opacity-0 group-hover:opacity-100">
                  <span className="brutal-border bg-[#E5E7EB] px-3 py-1 text-xs font-bold uppercase tracking-wider">
                    {p.tag}
                  </span>
                  <h3 className="font-display text-3xl text-white mt-3">{p.title}</h3>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#475569] text-white py-20 sm:py-28">
        <div className="max-w-7xl mx-auto px-6 sm:px-10 text-center">
          <h2 className="font-display text-5xl sm:text-7xl leading-none">
            Got a project in mind?
          </h2>
          <p className="mt-4 text-lg font-medium text-white/80 max-w-xl mx-auto">
            Tell us what you're dreaming up and we'll bring it to life with gifted hands.
          </p>
          <Link
            to="/contact"
            data-testid="cta-start-order"
            className="mt-8 inline-flex items-center gap-3 brutal-border brutal-shadow brutal-press bg-[#3E6FA8] text-white font-extrabold uppercase tracking-wider text-lg px-8 py-4"
          >
            Start Your Order <ArrowRight size={22} strokeWidth={2.5} />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
}
